import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { readMenuAll } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { localize } from 'common/utils';

import { DiscountedItem } from './useDiscountedItems';
import { useProductInformationSettings } from './useProductInformationSettings';
import { getItemCategory } from '../utils/menuItemsUtils';

export const useMenuItems = (menuId: string) => {
  const { locale } = useIntl();

  const { isFetching: isLoadingProductInformationSetting, errorProductInformationSettings } =
    useProductInformationSettings(menuId);

  const { data, isFetching, error } = useQuery(['read-menu-all', menuId], () => readMenuAll(menuId), {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const selectableItems =
    data?.items.reduce<DiscountedItem[]>((items, item) => {
      // Fall back to a dummy category to make sure all items are included for existing campaigns
      const itemCategory = getItemCategory(data.menu.categories, item.id) ?? { id: '', name: [] };
      items.push({
        ...item,
        name: localize(item.name, locale),
        description: localize(item.description, locale),
        category: itemCategory ? { id: itemCategory.id, name: localize(itemCategory.name, locale) } : undefined,
      });
      return items;
    }, []) ?? [];

  // Sort the items so that they are in the order of the categories
  const sortedItems: DiscountedItem[] =
    data?.menu.categories.flatMap(category => selectableItems.filter(item => item.category?.id === category.id)) ?? [];

  return {
    menuItems: sortedItems,
    isLoadingMenuData: isFetching || isLoadingProductInformationSetting,
    hasErrorMenuData: error || errorProductInformationSettings,
  };
};
